import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';

const { FiBarChart3, FiSettings, FiDollarSign, FiUsers, FiEye, FiTrendingUp, FiMonitor, FiGlobe, FiLock, FiShield, FiKey, FiAlertTriangle } = FiIcons;

function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [loginAttempts, setLoginAttempts] = useState(0);
  const [isLocked, setIsLocked] = useState(false);
  const [lockTimeRemaining, setLockTimeRemaining] = useState(0);
  const [analytics, setAnalytics] = useState({
    pageViews: 12543,
    uniqueVisitors: 8921,
    imagesResized: 45231,
    avgSessionDuration: '3m 42s'
  });
  const [adsenseCode, setAdsenseCode] = useState('');
  const [analyticsCode, setAnalyticsCode] = useState('');
  const [securityLog, setSecurityLog] = useState([]);

  // Get stored admin password or use default
  const getStoredPassword = () => {
    return localStorage.getItem('adminPassword') || 'BertoProduction2024!';
  };

  // Security lockout mechanism
  useEffect(() => {
    const lockEndTime = localStorage.getItem('adminLockEndTime');
    if (lockEndTime && Date.now() < parseInt(lockEndTime)) {
      setIsLocked(true);
      const remaining = Math.ceil((parseInt(lockEndTime) - Date.now()) / 1000);
      setLockTimeRemaining(remaining);
      
      const timer = setInterval(() => {
        const newRemaining = Math.ceil((parseInt(lockEndTime) - Date.now()) / 1000);
        if (newRemaining <= 0) {
          setIsLocked(false);
          setLockTimeRemaining(0);
          setLoginAttempts(0);
          localStorage.removeItem('adminLockEndTime');
          clearInterval(timer);
        } else {
          setLockTimeRemaining(newRemaining);
        }
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, []);

  const handleLogin = (e) => {
    e.preventDefault();
    
    if (isLocked) {
      alert(`Account is locked. Please wait ${Math.ceil(lockTimeRemaining / 60)} minutes.`);
      return;
    }

    const storedPassword = getStoredPassword();
    
    if (password === storedPassword) {
      setIsAuthenticated(true);
      setLoginAttempts(0);
      localStorage.setItem('adminAuth', 'true');
      localStorage.setItem('adminLoginTime', Date.now().toString());
      
      // Log successful login
      const newLog = {
        timestamp: new Date().toLocaleString(),
        action: 'Successful Login',
        ip: 'Client IP',
        status: 'Success'
      };
      setSecurityLog(prev => [newLog, ...prev.slice(0, 9)]);
      
      // Track login analytics
      if (typeof gtag !== 'undefined') {
        gtag('event', 'admin_login', {
          event_category: 'Admin',
          event_label: 'Successful Login'
        });
      }
    } else {
      const newAttempts = loginAttempts + 1;
      setLoginAttempts(newAttempts);
      
      // Log failed login
      const newLog = {
        timestamp: new Date().toLocaleString(),
        action: 'Failed Login Attempt',
        ip: 'Client IP',
        status: 'Failed'
      };
      setSecurityLog(prev => [newLog, ...prev.slice(0, 9)]);
      
      if (newAttempts >= 5) {
        // Lock account for 15 minutes
        const lockEndTime = Date.now() + (15 * 60 * 1000);
        localStorage.setItem('adminLockEndTime', lockEndTime.toString());
        setIsLocked(true);
        setLockTimeRemaining(900); // 15 minutes
        alert('Too many failed attempts. Account locked for 15 minutes.');
      } else {
        alert(`Invalid password. ${5 - newAttempts} attempts remaining.`);
      }
    }
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    
    if (passwordData.currentPassword !== getStoredPassword()) {
      alert('Current password is incorrect');
      return;
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    
    if (passwordData.newPassword.length < 8) {
      alert('New password must be at least 8 characters long');
      return;
    }
    
    // Store new password
    localStorage.setItem('adminPassword', passwordData.newPassword);
    
    // Log password change
    const newLog = {
      timestamp: new Date().toLocaleString(),
      action: 'Password Changed',
      ip: 'Client IP',
      status: 'Success'
    };
    setSecurityLog(prev => [newLog, ...prev.slice(0, 9)]);
    
    setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setShowPasswordChange(false);
    alert('Password changed successfully!');
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    localStorage.removeItem('adminAuth');
    localStorage.removeItem('adminLoginTime');
    setPassword('');
    
    // Log logout
    const newLog = {
      timestamp: new Date().toLocaleString(),
      action: 'Logout',
      ip: 'Client IP',
      status: 'Success'
    };
    setSecurityLog(prev => [newLog, ...prev.slice(0, 9)]);
  };

  // Check authentication on mount
  useEffect(() => {
    const auth = localStorage.getItem('adminAuth');
    const loginTime = localStorage.getItem('adminLoginTime');
    
    // Auto-logout after 24 hours
    if (auth === 'true' && loginTime) {
      const timeDiff = Date.now() - parseInt(loginTime);
      if (timeDiff > 24 * 60 * 60 * 1000) { // 24 hours
        handleLogout();
      } else {
        setIsAuthenticated(true);
      }
    }
  }, []);

  if (!isAuthenticated) {
    return (
      <>
        <SEOHead
          title="Admin Panel - Secure Access Required"
          description="Secure admin panel for ResizeImageFast website management. Protected access for authorized administrators only."
          keywords="admin panel, secure access, website management, administrator login"
          url="/admin"
          type="article"
        />
        
        <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-indigo-100">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8"
          >
            <div className="text-center mb-8">
              <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiShield} className="text-blue-600 text-2xl" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Secure Admin Access</h1>
              <p className="text-gray-600 mt-2">Protected dashboard for site management</p>
            </div>

            {isLocked ? (
              <div className="text-center p-6 bg-red-50 rounded-lg">
                <SafeIcon icon={FiAlertTriangle} className="text-red-500 text-3xl mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-red-800 mb-2">Account Locked</h3>
                <p className="text-red-600 mb-4">Too many failed login attempts</p>
                <div className="text-2xl font-bold text-red-700">
                  {Math.floor(lockTimeRemaining / 60)}:{(lockTimeRemaining % 60).toString().padStart(2, '0')}
                </div>
                <p className="text-sm text-red-500 mt-2">Time remaining</p>
              </div>
            ) : (
              <form onSubmit={handleLogin} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    <SafeIcon icon={FiLock} className="inline mr-1" />
                    Admin Password
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Enter admin password"
                    required
                  />
                  {loginAttempts > 0 && (
                    <p className="text-red-500 text-sm mt-1">
                      {loginAttempts} failed attempt{loginAttempts > 1 ? 's' : ''}. {5 - loginAttempts} remaining.
                    </p>
                  )}
                </div>
                
                <button
                  type="submit"
                  disabled={isLocked}
                  className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:bg-gray-400"
                >
                  <SafeIcon icon={FiKey} className="inline mr-2" />
                  Secure Login
                </button>
              </form>
            )}

            <div className="mt-6 text-center">
              <p className="text-xs text-gray-500">
                🔒 Protected by BertoProduction Security
              </p>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  return (
    <>
      <SEOHead
        title="Admin Dashboard - Site Management Panel"
        description="Secure admin dashboard for managing ResizeImageFast website analytics, settings, and configuration."
        keywords="admin dashboard, website management, analytics dashboard, site administration"
        url="/admin"
        type="article"
      />
      
      <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-between items-center mb-8"
          >
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">Secure management panel for Free Image Resizer</p>
            </div>
            <div className="flex space-x-4">
              <button
                onClick={() => setShowPasswordChange(!showPasswordChange)}
                className="bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors flex items-center"
              >
                <SafeIcon icon={FiKey} className="mr-2" />
                Change Password
              </button>
              <button
                onClick={handleLogout}
                className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors flex items-center"
              >
                <SafeIcon icon={FiLock} className="mr-2" />
                Secure Logout
              </button>
            </div>
          </motion.div>

          {/* Password Change Modal */}
          {showPasswordChange && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
            >
              <div className="bg-white rounded-2xl p-8 max-w-md w-full mx-4">
                <h2 className="text-xl font-bold text-gray-900 mb-6">Change Admin Password</h2>
                <form onSubmit={handlePasswordChange} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Current Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.currentPassword}
                      onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      New Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.newPassword}
                      onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      minLength="8"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm New Password
                    </label>
                    <input
                      type="password"
                      value={passwordData.confirmPassword}
                      onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    />
                  </div>
                  <div className="flex space-x-4 pt-4">
                    <button
                      type="submit"
                      className="flex-1 bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Update Password
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowPasswordChange(false)}
                      className="flex-1 bg-gray-300 text-gray-700 py-3 rounded-lg hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </motion.div>
          )}

          {/* Stats Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {[
              { icon: FiEye, label: 'Page Views', value: analytics.pageViews.toLocaleString(), color: 'blue' },
              { icon: FiUsers, label: 'Unique Visitors', value: analytics.uniqueVisitors.toLocaleString(), color: 'green' },
              { icon: FiBarChart3, label: 'Images Resized', value: analytics.imagesResized.toLocaleString(), color: 'purple' },
              { icon: FiTrendingUp, label: 'Avg. Session', value: analytics.avgSessionDuration, color: 'orange' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-2xl shadow-lg p-6"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className={`bg-${stat.color}-100 w-12 h-12 rounded-lg flex items-center justify-center`}>
                    <SafeIcon icon={stat.icon} className={`text-${stat.color}-600 text-xl`} />
                  </div>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
                <p className="text-gray-600">{stat.label}</p>
              </motion.div>
            ))}
          </div>

          {/* Security Log */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6 mb-8"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiShield} className="mr-2" />
              Security Activity Log
            </h2>
            <div className="space-y-3">
              {securityLog.length > 0 ? securityLog.map((log, index) => (
                <div key={index} className="flex justify-between items-center p-3 bg-gray-50 rounded-lg">
                  <div>
                    <span className="font-medium">{log.action}</span>
                    <span className="text-gray-500 text-sm ml-2">from {log.ip}</span>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-600">{log.timestamp}</div>
                    <span className={`px-2 py-1 rounded text-xs ${
                      log.status === 'Success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {log.status}
                    </span>
                  </div>
                </div>
              )) : (
                <p className="text-gray-500 text-center py-4">No recent security activity</p>
              )}
            </div>
          </motion.div>

          {/* Settings Panels */}
          <div className="grid lg:grid-cols-2 gap-8">
            {/* Analytics Settings */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl shadow-lg p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <SafeIcon icon={FiBarChart3} className="mr-2" />
                Analytics Settings
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Google Analytics Code
                  </label>
                  <textarea
                    value={analyticsCode}
                    onChange={(e) => setAnalyticsCode(e.target.value)}
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="<!-- Google Analytics tracking code -->"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Paste your GA4 tracking code here
                  </p>
                </div>

                <button className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors">
                  Save Analytics Settings
                </button>
              </div>
            </motion.div>

            {/* AdSense Settings */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-2xl shadow-lg p-6"
            >
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <SafeIcon icon={FiDollarSign} className="mr-2" />
                AdSense Settings
              </h2>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    AdSense Publisher ID
                  </label>
                  <input
                    type="text"
                    value={adsenseCode}
                    onChange={(e) => setAdsenseCode(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="ca-pub-xxxxxxxxxxxxxxxxx"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Ad Unit Code
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="<ins class='adsbygoogle'..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Paste your AdSense ad unit code here
                  </p>
                </div>

                <button className="bg-green-600 text-white px-6 py-3 rounded-lg hover:bg-green-700 transition-colors">
                  Save AdSense Settings
                </button>
              </div>
            </motion.div>
          </div>

          {/* Site Configuration */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6 mt-8"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiGlobe} className="mr-2" />
              Site Configuration
            </h2>

            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Site Title
                </label>
                <input
                  type="text"
                  defaultValue="Free Image Resizer Online - BertoProduction"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Meta Description
                </label>
                <input
                  type="text"
                  defaultValue="Resize images online for free! Professional tool with custom dimensions and high quality."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Keywords
                </label>
                <input
                  type="text"
                  defaultValue="image resizer, resize images, free tool, photo editor"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Email
                </label>
                <input
                  type="email"
                  defaultValue="info@bertoproduction.com"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="mt-6">
              <button className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors">
                Save Configuration
              </button>
            </div>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-2xl shadow-lg p-6 mt-8"
          >
            <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
              <SafeIcon icon={FiMonitor} className="mr-2" />
              Quick Actions
            </h2>

            <div className="grid md:grid-cols-4 gap-4">
              <button className="bg-blue-100 text-blue-700 px-6 py-4 rounded-lg hover:bg-blue-200 transition-colors flex items-center justify-center">
                <SafeIcon icon={FiBarChart3} className="mr-2" />
                View Analytics
              </button>
              
              <button className="bg-green-100 text-green-700 px-6 py-4 rounded-lg hover:bg-green-200 transition-colors flex items-center justify-center">
                <SafeIcon icon={FiDollarSign} className="mr-2" />
                Revenue Report
              </button>
              
              <button className="bg-purple-100 text-purple-700 px-6 py-4 rounded-lg hover:bg-purple-200 transition-colors flex items-center justify-center">
                <SafeIcon icon={FiSettings} className="mr-2" />
                Site Settings
              </button>

              <button className="bg-red-100 text-red-700 px-6 py-4 rounded-lg hover:bg-red-200 transition-colors flex items-center justify-center">
                <SafeIcon icon={FiShield} className="mr-2" />
                Security Log
              </button>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
}

export default AdminPanel;